import "./redeem-custom.scss";

import { MessageResponseDto } from "@api/api";
import { REDEEM_IS_CUSTOM } from "@core/model/const";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import AnimatedCounter from "@core/ui/animated-counter/animated-counter";
import { GetResponseBranchModel } from "@factory/model/query-model-branch";
import { UserBalanceResponse } from "@factory/model/query-model-reward";
import { useQyGetBranch } from "@factory/query/branch.query";
import {
  useQyGetUserBalance,
  useQyRedeemReward,
} from "@factory/query/reward.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { LabelValue } from "@shared/models/label-value.interface";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import Scan from "@shared/ui/scan/scan";
import useScreenSize from "@shared/utility/screen-size";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { appFeature } from "../../../app.feature";

export interface RedeemCustomProps {}

export function RedeemCustom() {
  // DECLARATIONS
  const { getUserRole } = useUserIdentityContext();
  const { tenant } = getUserRole();
  const { showSuccess, showWarning } = useNotificationContext();
  const { mapRedeemReward } = useFormApiMapper();
  const navigate = useNavigate();
  const { isMobileMode } = useScreenSize();
  const [scan, setScan] = useState(false);
  const [scanCardAlias, setScanCardAlias] = useState("");
  const [amount, setAmount] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [cardUserInformation, setCardUserInformation] =
    useState<UserBalanceResponse | null>(null);
  const [branchOption, seBranchOption] = useState<LabelValue[]>([]);
  const { pointsText, stampsText } = appFeature.config.label;

  // API DEFINITIONS
  const handleSuccess = (response: GetResponseBranchModel) => {
    const responseData = response?.data || [];
    if (responseData.length === 0) {
      return;
    }

    setSelectedBranch(responseData?.[0].code);
    const _branchOption = LabelValue.MapArray(
      response?.data || [],
      (item) => item.name,
      (item) => item.code
    );
    seBranchOption(_branchOption);
  };
  useQyGetBranch("", 9999, 0, undefined, undefined, true, handleSuccess);

  const handleGetMemberBalance = (response: UserBalanceResponse) => {
    setCardUserInformation(response);
  };
  useQyGetUserBalance(
    scanCardAlias,
    tenant,
    !!scanCardAlias,
    handleGetMemberBalance
  );

  // API - REDEEM
  const handleRedeemSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`Redemption complete`);
    navigate(
      `../redeem-code-success?points=${data.points}&stamp=${data.stamp}&return=campaign`
    );
  };
  const { mutate: redeem } = useQyRedeemReward(handleRedeemSuccess);

  // LOCAL FUNCTIONS
  const handleBack = () => {
    navigate("/business/campaign");
  };
  const handleSuccessScan = (value: string) => {
    setScanCardAlias(value);
  };
  const handleCloseScan = () => {
    setScan(false);
  };
  const handleRedeem = () => {
    if (!scanCardAlias || !amount) {
      showWarning("No amount or card number found");
      return;
    }

    const parsedAmount = parseInt(amount ?? 0, 10);
    const value = mapRedeemReward(
      scanCardAlias,
      REDEEM_IS_CUSTOM,
      selectedBranch,
      tenant,
      "",
      parsedAmount,
      0
    );
    redeem(value);
  };

  // LOCAL RENDERS
  const footerContent = (
    <div>
      <Button
        className="block w-full mt-2"
        label="Redeem"
        onClick={() => handleRedeem()}
      />
      <Button
        className="block w-full mt-2"
        label="Close"
        severity="secondary"
        outlined
        onClick={() => handleBack()}
      />
    </div>
  );
  const cardDetail = (
    <div id="card-detail" className="border border-dashed p-4">
      <h3 className="text-center mb-2">
        {cardUserInformation?.nickname ??
          `${cardUserInformation?.first_name} ${cardUserInformation?.last_name}`}
      </h3>
      <div className="flex justify-center gap-4">
        <AnimatedCounter
          from={0}
          to={cardUserInformation?.card_current_points ?? 0}
          duration={1}
          delay={1}
          label={pointsText}
        />
        <Divider layout="vertical" />
        <AnimatedCounter
          from={0}
          to={parseInt(cardUserInformation?.card_current_stamp ?? "0", 10) ?? 0}
          duration={1}
          delay={1}
          label={stampsText}
        />
      </div>
    </div>
  );

  return (
    <div id="RedeemCustom" className="redeem-custom">
      <ConfirmDialog />
      <Dialog
        header="Custom Redeem"
        visible={true}
        style={{ width: "50vw" }}
        onHide={() => {
          handleBack();
        }}
        closeOnEscape={true}
        footer={footerContent}
        closable={false}
        maximized={isMobileMode}
      >
        <div className="m-0">
          {scan ? (
            <Scan
              successScan={handleSuccessScan}
              closeScan={handleCloseScan}
              isCloseOnScan={true}
            />
          ) : null}
          <div className="mt-6"></div>
          <div className="mb-6">
            <div className="p-inputgroup flex-1">
              <FloatLabel>
                <InputText
                  id="cardAlias"
                  value={scanCardAlias}
                  onChange={(e) => setScanCardAlias(e.target.value)}
                  className="text-4xl w-full"
                  autoFocus
                  tabIndex={0}
                />
                <label htmlFor="cardAlias">Enter Card Number</label>
              </FloatLabel>

              <span
                className="p-inputgroup-addon cursor-pointer px-8 bg-emerald-500 border border-emerald-400"
                onClick={() => setScan(true)}
              >
                <i className="pi pi-camera text-white"></i>
              </span>
            </div>
          </div>

          {cardUserInformation ? cardDetail : null}

          <Dropdown
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.value)}
            options={branchOption}
            placeholder="Select a branch"
            className="w-full md:w-14rem mt-6 text-4xl"
          />

          <div className="mt-6">
            <div className="p-inputgroup flex-1">
              <FloatLabel>
                <InputText
                  id="amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="text-4xl w-full"
                  keyfilter="int"
                />
                <label htmlFor="amount">Enter {pointsText} to Redeem</label>
              </FloatLabel>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default RedeemCustom;
